import { z } from 'zod'
import {
  acreageSchema,
  measuredQuantitySchema,
  trunkDiameterSchema,
} from '../validators/decimal-string.validator.js'
import { moneyAmountSchema } from '../validators/money.validator.js'
import { taxRatePercentSchema } from '../validators/tax-rate.validator.js'
import { uuidSchema } from '../validators/uuid.validator.js'
import { problemSeveritySchema } from '../enums/problem-severity.enum.js'
import { jobLevelSchema, jobTypeSchema } from '../enums/job-type.enum.js'
import { seasonSchema } from '../enums/season.enum.js'
import { treeSpeciesMarkSchema } from './tree-species.dto.js'
import { siteBoundarySchema, geoPointSchema } from './geo.dto.js'
import { siteFeaturesSchema } from './site-features.dto.js'

/**
 * A proposal, resolved into the blocks that get rendered.
 *
 * This is the RESOLVED form — every block already carries the values it
 * displays. Nothing here is a reference the renderer has to go and look up, and
 * no block holds an id that would send it back to the database. That is the
 * whole point: a proposal document is a thing that can be frozen, stored, and
 * re-rendered years later to exactly what the client was sent.
 *
 * The composition — which blocks, in what order, with what authored copy —
 * lives in code (`api/src/proposals/document/proposal-template.ts`). There is
 * deliberately no editor: proposals for one tree-care business selling one kind
 * of work do not each need a different shape, and an editor is a large amount
 * of surface area to maintain so that a sentence can be changed without a
 * deploy.
 *
 * ## Why blocks at all, rather than one HTML string
 *
 * Half of a proposal is derived from data the app already holds — the site map,
 * the numbered tree schedule, the hours, the price. Those cannot be prose,
 * because they have to stay correct as a survey changes, and they have to be
 * STYLED rather than pasted. The other half is authored sentences. A block
 * union lets both live in one ordered document without pretending either is the
 * other.
 */

/** A tree as it appears on a map block. Trimmed to what gets drawn. */
export const documentTreeSchema = z.object({
  /**
   * Which tree this is, by id: identity, never drawn or read back, kept so a
   * job made from the proposal can say which trees its work was done on
   * (docs/site-visits.md, "Tree work done"). Everything the page shows is by
   * value. Null on documents frozen before it was kept.
   */
  treeId: z.string().nullable().optional(),
  label: z.string(),
  /**
   * The species by VALUE — name, shape, color — never by id. Null when the
   * tree was unidentified.
   *
   * This is the snapshot rule applied to a lookup. Species are configured in
   * the app, so one can be renamed, re-colored or archived at any time; a
   * frozen proposal holding an id would re-render years later with a mark the
   * client never saw, on a map already sitting in a board's minutes. Holding
   * the values means the document keeps saying what it said.
   */
  species: treeSpeciesMarkSchema.nullable(),
  latitude: geoPointSchema.shape.latitude,
  longitude: geoPointSchema.shape.longitude,
  jobs: z.array(
    z
      .object({
        type: jobTypeSchema,
        level: jobLevelSchema,
        /**
         * Minutes for this job, when the arborist split the tree's time up.
         *
         * In the SNAPSHOT because a client declining a kind of work does the
         * arithmetic against the document they were sent, not against a tree
         * that may have been re-surveyed since.
         *
         * `.default(null)` — a new required key would make every document
         * frozen before today fail to parse, silently un-freezing proposals
         * already sent.
         */
        minutes: z.number().int().nonnegative().nullable().default(null),
        notes: z.string().nullable().default(null),
      })
      .strict(),
  ),
  /** Whole minutes, or null when nobody has estimated it yet. */
  estimatedLaborMinutes: z.number().int().nonnegative().nullable(),
  /**
   * The arborist recommends no pruning on this tree. Absent, as on every
   * document built before it could be said, is false.
   */
  noPruningRecommended: z.boolean().optional(),
  /**
   * The pruning price set by hand, in place of the estimate at the crew rate.
   * Absent or null is the crew rate.
   */
  pruningPriceOverride: moneyAmountSchema.nullable().optional(),
  /**
   * Removal, when the arborist offered it. Null when they did not.
   *
   * `.default(null)` — documents frozen before removal existed offered none,
   * and a required key would make every one of them fail to parse.
   */
  removal: z
    .object({
      minutes: z.number().int().nonnegative().nullable(),
      notes: z.string().nullable(),
      /** The removal price set by hand; absent or null is the crew rate. */
      priceOverride: moneyAmountSchema.nullable().optional(),
    })
    .strict()
    .nullable()
    .default(null),
  /**
   * What was found wrong, by VALUE — never by id, exactly like `species`.
   *
   * The same snapshot rule, for the same reason. A problem type's description
   * and solution are edited in settings and are the sentences a client read
   * when they agreed a price; holding an id would let a frozen proposal
   * re-render years later quoting wording nobody showed them.
   */
  problems: z.array(
    z
      .object({
        name: z.string(),
        description: z.string().nullable(),
        solution: z.string().nullable(),
        severity: problemSeveritySchema.nullable(),
        /** What the surveyor wrote about this tree specifically. */
        notes: z.string().nullable(),
        photoDocumentId: uuidSchema.nullable(),
        /**
         * Every photograph taken of this problem on this tree, oldest first.
         *
         * Unlike `photoDocumentId` this never falls back to the directory's
         * stock image: these are the evidence, and a stock picture presented
         * as a photo of the client's tree would be a claim nobody made.
         *
         * `.default([])` — documents frozen before photos were carried have
         * no such key.
         */
        photoDocumentIds: z.array(uuidSchema).default([]),
        /**
         * What dealing with it costs on this tree, set by hand in place of
         * the treatments' sum. Absent or null is the sum.
         */
        priceOverride: moneyAmountSchema.nullable().optional(),
        /**
         * The treatments chosen, by VALUE like everything else here.
         *
         * `.default([])` because a document frozen before solutions existed
         * has no such key, and a new REQUIRED field would make every one of
         * them fail to parse — silently un-freezing proposals already sent.
         */
        solutions: z
          .array(
            z
              .object({
                label: z.string(),
                description: z.string().nullable(),
                /** Crew minutes this treatment adds to the tree. */
                minutes: z.number().int().nonnegative().nullable(),
                /** Exact decimal string; null when it could not be priced. */
                amount: moneyAmountSchema.nullable(),
              })
              .strict(),
          )
          .default([]),
      })
      .strict(),
  ),
  /**
   * Trunk diameter at breast height, in inches, as an exact decimal string.
   *
   * Carried so the site plan can draw each canopy at roughly its real spread.
   * Null when nobody measured it, and on documents frozen before it was
   * carried.
   */
  dbhInches: trunkDiameterSchema.nullable().default(null),
  /**
   * Photographs of the tree itself, oldest first. Problem photos are on the
   * problem they show.
   *
   * Ids, not URLs: storage is private and links expire, so the page asks for
   * a fresh link through a route that checks the id is on this document.
   */
  photoDocumentIds: z.array(uuidSchema).default([]),
  /**
   * Videos of this tree that finished processing, by id, with their length.
   *
   * Ids for the same reason photos are: Stream playback links are signed and
   * expire, so the page asks for one when somebody presses play, through a
   * route that checks the id is on this document.
   */
  videos: z
    .array(
      z
        .object({
          id: uuidSchema,
          durationSeconds: z.number().int().nonnegative().nullable(),
        })
        .strict(),
    )
    .default([]),
  /**
   * The arborist offers this work but does not call it necessary. False on
   * documents frozen before the flag existed, which is what they meant.
   */
  isOptional: z.boolean().default(false),
})

export type DocumentTree = z.infer<typeof documentTreeSchema>

/**
 * The cover.
 *
 * `preparedFor` names a person, not just the client: an HOA proposal is read by
 * a community manager who has to carry it to a board, and addressing the
 * management company rather than the human handling the property is the first
 * sign a bid was mailed rather than written.
 */
export const coverBlockSchema = z.object({
  type: z.literal('cover'),
  title: z.string(),
  clientName: z.string(),
  preparedForName: z.string().nullable(),
  preparedForTitle: z.string().nullable(),
  preparedByName: z.string().nullable(),
  preparedByOrganization: z.string(),
  /**
   * The letterhead logo, or null when none is set.
   *
   * A URL rather than a document id, and captured into the frozen document at
   * send. The URL addresses one SPECIFIC uploaded file, so replacing the logo
   * later leaves an already-sent proposal showing the mark it actually went
   * out with — which is the same promise the rest of the snapshot makes.
   *
   * `preparedByOrganization` stays either way. It is what the logo is
   * replaced BY when there is one, and the fallback when there is not, and a
   * document that cannot name its author if an image fails to load is a
   * document with a single point of failure in its letterhead.
   *
   * `.default(null)`, and this is NOT decoration. Sent versions store a
   * rendered document as JSON and it is parsed back with this schema; a new
   * REQUIRED key makes every snapshot written before today fail to parse, and
   * the reader falls back to resolving live — silently un-freezing every
   * proposal already sent. A default is what makes adding a field additive
   * for documents that are already in clients' hands. Any field added to this
   * schema from here on needs the same treatment.
   */
  logoUrl: z.string().nullable().default(null),
  /**
   * The photograph the proposal opens on, or null to open on the site plan.
   *
   * The first photo attached to the version, so staff choose it the way they
   * already attach photos, with no new control. Defaulted for documents sent
   * before it existed.
   */
  coverPhotoDocumentId: uuidSchema.nullable().default(null),
  /**
   * The preparer's credential, written out ("ISA Certified Arborist
   * TX-4745A"). Null when none is recorded, in which case nothing is claimed.
   */
  preparedByCredential: z.string().nullable().default(null),
  /** The number a client should call, as the business writes it. */
  phone: z.string().nullable().default(null),
  /**
   * When the trees in this proposal were recorded: the latest day any of
   * them was added. Null for documents frozen before it was carried.
   */
  surveyedOn: z.string().datetime().nullable().default(null),
  versionNumber: z.number().int().positive(),
  issuedOn: z.string().datetime(),
  validUntil: z.string().datetime().nullable(),
  sites: z.array(
    z
      .object({
        name: z.string(),
        address: z.string().nullable(),
      })
      .strict(),
  ),
})

/**
 * The at-a-glance figures.
 *
 * Written to stand alone, because for some board members it is the only block
 * they read before voting.
 *
 * `hoursAreComplete` is false when any tree in scope has no estimate. The
 * renderer marks the total with a `+` rather than silently presenting a partial
 * figure as the finished one — the same rule the survey header uses.
 */
export const summaryBlockSchema = z.object({
  type: z.literal('summary'),
  siteCount: z.number().int().nonnegative(),
  treeCount: z.number().int().nonnegative(),
  jobCount: z.number().int().nonnegative(),
  /** Total acreage across the sites, or null when none has been drawn. */
  acreage: acreageSchema.nullable(),
  /**
   * Crew time, in minutes.
   *
   * Minutes on the wire even though the document prints hours: a total is the
   * sum of ninety integers, and converting each block's figure at the last
   * moment means the renderer and the price cannot round differently.
   */
  totalMinutes: z.number().int().nonnegative(),
  hoursAreComplete: z.boolean(),
  /**
   * The price of the work, before tax. Null when nothing could be priced.
   *
   * The page adds the tax rate from the pricing block when it draws this, so
   * the figure a board member sees here is what the proposal actually costs.
   */
  totalAmount: moneyAmountSchema.nullable(),
  /**
   * Trees with at least one problem rated Serious or Critical.
   *
   * Null on documents frozen before it was counted, so an old proposal does
   * not start claiming it found no hazards.
   */
  hazardTreeCount: z.number().int().nonnegative().nullable().default(null),
  /**
   * Homes in the community, for the price per home. Null when nobody has
   * recorded it, which hides the figure rather than dividing by a guess.
   */
  homeCount: z.number().int().positive().nullable().default(null),
  /**
   * Irrigation repairs in scope. Absent on documents built before irrigation
   * could be bid, which carried none; `totalAmount` includes their price.
   */
  irrigationRepairCount: z.number().int().nonnegative().optional(),
})

/**
 * Which section of the wording a prose block is.
 *
 * The page places prose by what it is for: the narrative opens the proposal,
 * reassurance sits after the price, exclusions and terms go to the appendix.
 * Before roles existed that was implied by position, which a layout that
 * moves sections around cannot rely on.
 */
export const PROSE_ROLES = [
  'narrative',
  'exclusions',
  'access',
  'preparation',
  'standards',
  'terms',
  'comparison',
  'assurances',
  'nextSteps',
  'reviews',
] as const

export const proseRoleSchema = z.enum(PROSE_ROLES)

export type ProseRole = z.infer<typeof proseRoleSchema>

/** Authored prose. The condition narrative, exclusions, access notes, site preparation and safety, terms. */
export const proseBlockSchema = z.object({
  type: z.literal('prose'),
  /**
   * Null on documents frozen before roles existed. The page then reads the
   * first prose block as the narrative and treats the rest by title.
   */
  role: proseRoleSchema.nullable().default(null),
  title: z.string().nullable(),
  /** Paragraphs, as plain text. Rendered one `<p>` each — no markup to trust. */
  paragraphs: z.array(z.string()),
  /** Optional bullets under the paragraphs. */
  bullets: z.array(z.string()),
})

/**
 * The drawn site.
 *
 * The block nobody bidding against this can produce. A board looking at a map
 * with ninety numbered pins is looking at evidence; a board looking at a
 * paragraph is being asked to take somebody's word for it.
 */
export const siteMapBlockSchema = z.object({
  type: z.literal('siteMap'),
  siteName: z.string(),
  boundary: siteBoundarySchema.nullable(),
  trees: z.array(documentTreeSchema),
  /** Pre-computed so the renderer never has to derive a frame. */
  center: geoPointSchema.nullable(),
  acreage: acreageSchema.nullable(),
  /**
   * Areas drawn on this site, frozen with the document.
   *
   * Absent on a document sent before areas existed. A draft resolves them
   * live; a sent copy keeps the rings the client was shown, so redrawing a
   * courtyard later does not move a tree on a bid they already have.
   */
  areas: z
    .array(
      z
        .object({
          id: z.string(),
          name: z.string(),
          boundary: siteBoundarySchema,
        })
        .strict(),
    )
    .optional(),
  /**
   * Roads, buildings and water around the trees, frozen with the document.
   *
   * Null when none were fetched (no boundary, or the lookup found nothing),
   * and on documents sent before the site plan existed. The page then draws
   * the trees on their own, with the aerial photo a tap away.
   */
  features: siteFeaturesSchema.nullable().default(null),
  /**
   * The irrigation repairs, where they were found, and the outlines of the
   * zones that hold them, frozen with the document.
   *
   * Pins only. What each repair is and what it costs are on the site's
   * irrigation schedule, joined by key, as a tree's row is joined by its
   * label. A repair with nowhere to stand is not pinned; it is still listed
   * and priced. Null when the proposal carries no irrigation here, and on
   * documents sent before the plan drew it, which show the trees alone.
   */
  irrigation: z
    .object({
      zones: z.array(
        z.object({ name: z.string(), boundary: siteBoundarySchema }).strict(),
      ),
      repairs: z.array(geoPointSchema.extend({ key: z.string() }).strict()),
    })
    .strict()
    .nullable()
    .default(null),
})

/** One row of the numbered schedule. Ordered to collate against the map. */
export const treeScheduleRowSchema = z.object({
  /** Which tree, by id, as on `documentTreeSchema`; null on older documents. */
  treeId: z.string().nullable().optional(),
  label: z.string(),
  /** By value, not by id — see `documentTreeSchema`. */
  species: treeSpeciesMarkSchema.nullable(),
  jobs: z.array(
    z
      .object({
        type: jobTypeSchema,
        level: jobLevelSchema,
        /**
         * Minutes for this job, when the arborist split the tree's time.
         *
         * Here as well as on `documentTreeSchema` because this is the row a
         * CLIENT does arithmetic against when they decline a kind of work.
         * The schedule is what they are looking at, and the figures have to
         * come from the document they were sent rather than from a survey
         * that may have moved on.
         */
        minutes: z.number().int().nonnegative().nullable().default(null),
      })
      .strict(),
  ),
  estimatedLaborMinutes: z.number().int().nonnegative().nullable(),
  /**
   * The arborist recommends no pruning on this tree, which the schedule says
   * in place of a list of work. Absent on older documents, meaning false.
   */
  noPruningRecommended: z.boolean().optional(),
  /**
   * The pruning price, when the arborist set it by hand rather than the crew
   * rate pricing the minutes. Absent or null is the crew rate.
   *
   * Carried rather than folded into the minutes because the client does the
   * arithmetic: declining a kind of work on an itemized tree takes that
   * job's share of this figure, by its minutes (`priceSelection`).
   */
  pruningPriceOverride: moneyAmountSchema.nullable().optional(),
  /**
   * Removal, when the arborist offered it on this tree.
   *
   * Null when they did not, which is what keeps the client's page readable:
   * a choice is only shown where there is one to make, so a ninety-tree
   * proposal does not become ninety rows of controls.
   *
   * `.default(null)` — documents frozen before removal existed offered none.
   */
  removal: z
    .object({
      minutes: z.number().int().nonnegative().nullable(),
      notes: z.string().nullable(),
      /** Exact decimal string; null when it could not be priced. */
      amount: moneyAmountSchema.nullable(),
    })
    .strict()
    .nullable()
    .default(null),
  /**
   * What each problem on this tree costs to treat.
   *
   * By problem NAME, matching the way the problems block groups them, so a
   * client declining "Mistletoe" is declining the same thing in both places.
   * The amount is the sum of that problem's treatments on this tree.
   */
  problems: z
    .array(
      z
        .object({
          name: z.string(),
          /** Exact decimal string; null when nothing here could be priced. */
          amount: moneyAmountSchema.nullable(),
          /**
           * How bad it is on this tree. Carried so a plan can take the
           * hazards first; null when unrated and on older documents.
           */
          severity: problemSeveritySchema.nullable().default(null),
        })
        .strict(),
    )
    .default([]),
  /**
   * The area this tree was in when the document was built.
   *
   * Optional, and null when the tree sat in no area. A required key would
   * make every document frozen before areas existed fail to parse. The id is
   * the one frozen on this document, not a live row: an area renamed or
   * redrawn afterwards must not change which trees a sent bid grouped.
   */
  areaId: z.string().nullable().optional(),
  /** The area's name at the same moment. Null when `areaId` is. */
  areaName: z.string().nullable().optional(),
  /**
   * Offered but not recommended. Carried on the row because the plans read
   * the schedule: optional trees sit outside "Everything recommended".
   */
  isOptional: z.boolean().default(false),
})

export const treeScheduleBlockSchema = z.object({
  type: z.literal('treeSchedule'),
  siteName: z.string(),
  rows: z.array(treeScheduleRowSchema),
  subtotalMinutes: z.number().int().nonnegative(),
  hoursAreComplete: z.boolean(),
  /**
   * The season of the visit these rows are for, when the site's work has
   * seasons (docs/seasonal-work.md). Work for later seasons is not in the
   * rows; it is under Future work.
   *
   * Null or absent when nothing here has a season, as on every document
   * frozen before seasons existed.
   */
  season: seasonSchema.nullable().optional(),
})

/**
 * What the job names mean, in plain language.
 *
 * Only the job types actually present in this proposal. A legend explaining
 * four kinds of pruning when the work is one kind reads as a brochure, and a
 * board that has to decode vocabulary defers instead of voting.
 */
export const jobLegendBlockSchema = z.object({
  type: z.literal('jobLegend'),
  entries: z.array(
    z
      .object({
        jobType: jobTypeSchema,
        label: z.string(),
        description: z.string(),
      })
      .strict(),
  ),
})

/**
 * What was found wrong, and what is proposed about it.
 *
 * The place a problem earns its way into a client document. The site map
 * deliberately does NOT mark problems — an unexplained warning beside a pin
 * reads as a defect in OUR work — so this is the "narrative where there is
 * room to say what it is" that decision pointed at.
 *
 * Grouped by KIND of problem rather than listed per tree. Ninety lines of
 * "#41 mistletoe / #42 mistletoe" is an inventory; "mistletoe, on 31 trees,
 * here is what it is and what we do" is an argument. The affected tree labels
 * ride along so a reader can still tie it back to the map.
 *
 * Only problems present in this proposal's trees appear, for the same reason
 * the job legend is filtered: explaining a problem nobody is being charged for
 * reads as a brochure.
 */
export const problemsBlockSchema = z.object({
  type: z.literal('problems'),
  entries: z.array(
    z
      .object({
        /** The directory name, or "Other" for free-text findings. */
        name: z.string(),
        description: z.string().nullable(),
        /**
         * The problem-level wording from the directory.
         *
         * Nothing on the proposal prints it any more: the treatments below say
         * what is done about the problem, in more detail. It is kept, here and
         * in the directory, until the field itself is retired.
         */
        solution: z.string().nullable(),
        /**
         * The itemized treatments chosen for this kind of problem, each with
         * how many of its trees it covers, in the order they are first met.
         *
         * `.default([])`: a document frozen before this existed has no such
         * key, and a required one would make every one of them fail to parse
         * and quietly stop being frozen.
         */
        treatments: z
          .array(
            z
              .object({
                label: z.string(),
                description: z.string().nullable(),
                treeCount: z.number().int().positive(),
              })
              .strict(),
          )
          .default([]),
        /** Labels of the trees carrying it, in schedule order. */
        treeLabels: z.array(z.string()),
        /**
         * The worst severity recorded for this problem anywhere in the
         * proposal, or null when none was rated.
         *
         * The worst rather than an average: a mean of a 5 and four 1s is a 2,
         * which describes none of the five trees and hides the one that
         * matters.
         */
        worstSeverity: problemSeveritySchema.nullable(),
        /** Notes a surveyor wrote about specific trees, if any. */
        notes: z.array(z.string()),
        /**
         * A photograph to illustrate it: one taken of an affected tree where
         * there is one, otherwise the directory's stock image.
         */
        photoDocumentId: uuidSchema.nullable(),
      })
      .strict(),
  ),
})

/**
 * Money, rolled up to the site.
 *
 * Per-site rather than per-tree deliberately. A board handed ninety priced
 * lines will negotiate ninety lines; a board handed a figure per community area
 * can approve the entrance drive this year and the back nine next, which is how
 * HOA budgets actually work. The hours behind each figure stay visible in the
 * schedule above, so the number is still shown its work.
 */
export const pricingBlockSchema = z.object({
  type: z.literal('pricing'),
  /** Dollars per crew-hour, or null when no rate is configured. */
  ratePerHour: moneyAmountSchema.nullable(),
  sites: z.array(
    z
      .object({
        name: z.string(),
        minutes: z.number().int().nonnegative(),
        hoursAreComplete: z.boolean(),
        /** Null when there is no rate to price against. */
        amount: moneyAmountSchema.nullable(),
        /**
         * The property this row belongs to.
         *
         * Absent on a document built before areas. Readers treat a missing
         * value as `name`, which is what that row always was: one line per
         * site, labeled with the site.
         */
        siteName: z.string().optional(),
        /**
         * The area name this row bills, when the proposal is itemized.
         *
         * Null for a whole-site row and for the trees that sit in no area.
         * Absent means the same as null.
         */
        areaName: z.string().nullable().optional(),
      })
      .strict(),
  ),
  totalMinutes: z.number().int().nonnegative(),
  /**
   * The price of the work, before tax.
   *
   * Tax is not folded in. The rate rides beside this figure and is applied
   * when the document is rendered, and again when a client changes what they
   * are buying, so the rounding happens once on the figure they agree to.
   */
  totalAmount: moneyAmountSchema.nullable(),
  /**
   * Sales tax as a percent ("8.250"), or null when this proposal charges none.
   *
   * Frozen with the labor rate. A settings change after send must not rewrite
   * a document a client already has, and the page a client reads and the API
   * that records their acceptance both take the rate from here — two copies
   * of the arithmetic would be two answers to what was agreed.
   *
   * `.default(null)`: a document frozen before tax existed has no such key,
   * and a required one would make every one of them fail to parse.
   */
  taxRatePercent: taxRatePercentSchema.nullable().default(null),
  /** How many trees in scope carry no estimate. Zero means the total is firm. */
  unestimatedTreeCount: z.number().int().nonnegative(),
  /**
   * The season of the visit this price is for, once per season: one season
   * on almost every proposal, since a property's sites are surveyed in the
   * same week. Empty when no work has a season; absent on documents frozen
   * before seasons existed, which reads the same.
   */
  nextSeasons: z.array(seasonSchema).optional(),
})

/**
 * A part on an irrigation repair, frozen by value: the name, unit price and
 * taxable flag Housecall Pro had when the proposal was built, so a price
 * changed there afterwards never reaches a proposal already sent.
 */
export const irrigationPartSchema = z
  .object({
    name: z.string(),
    /** Exact decimal string, at most two places, as the column stores it. */
    quantity: measuredQuantitySchema,
    /** Dollars, as an exact decimal string. */
    unitPrice: moneyAmountSchema,
    taxable: z.boolean(),
    /** The part had left Housecall Pro when this was built. Stops a send. */
    gone: z.boolean(),
    /**
     * The part's material in Housecall Pro, so the job created from an
     * accepted proposal itemizes this exact copy (the price book holds some
     * parts twice, at different prices). Null in documents sent before it
     * was frozen: defaulted, or every older snapshot would stop parsing.
     */
    hcpMaterialUuid: z.string().nullable().default(null),
  })
  .strict()

export type IrrigationPart = z.infer<typeof irrigationPartSchema>

/**
 * One open irrigation repair, as it is priced and chosen.
 *
 * The page never shows these as priced lines (see "Pricing rolls up to the
 * site" in proposals.md); they are here because the price is recomputed from
 * them whenever a client changes what they are buying, as tree rows are.
 */
export const irrigationRepairRowSchema = z
  .object({
    /** The problem's id: stable within this document, for a plan to name it. */
    key: z.string(),
    /** The directory name, or "Other": what a client declines by kind. */
    kind: z.string(),
    /** What it was recorded on, in the survey's words: "Clubhouse 4", "Timer A". */
    subject: z.string(),
    /** The zone it is in, when it is in one: what a client declines by zone. */
    zone: z.string().nullable(),
    /**
     * The area of its timer, by name. A decline by name matches this, and
     * means every area of that name.
     */
    areaName: z.string().nullable(),
    /**
     * That area's id. Two areas can share a name, so a decline by id matches
     * this when it is present. Null on a document frozen before the id was
     * stored, and when the timer sits in no area: those still match by name.
     */
    areaId: z.string().nullable().default(null),
    severity: problemSeveritySchema.nullable(),
    /** How many: fourteen bad nozzles are one repair of count 14. */
    count: z.number().int().positive(),
    laborMinutes: z.number().int().nonnegative().nullable(),
    parts: z.array(irrigationPartSchema),
    /**
     * The technician's first photo of this repair, for its card on the plan.
     * Never the kind's stock photo, which shows a repair like it rather than
     * this one; that is the finding's. Null when none was taken, and on
     * documents sent before the plan drew repairs.
     */
    photoDocumentId: uuidSchema.nullable().default(null),
  })
  .strict()

/**
 * A site's irrigation repairs and what they are priced by, frozen by value.
 *
 * The rate card is the labor service for the client's type and the drive
 * charge for the site's town, as Housecall Pro had them (see "Pricing" in
 * irrigation.md). One block per site with repairs, beside its tree schedule.
 */
export const irrigationScheduleBlockSchema = z.object({
  type: z.literal('irrigationSchedule'),
  siteName: z.string(),
  rateCard: z
    .object({
      labor: z
        .object({
          name: z.string(),
          pricePerHour: moneyAmountSchema,
          taxable: z.boolean(),
          /** The labor service in HCP, for the job. Null before it was frozen. */
          hcpServiceUuid: z.string().nullable().default(null),
          /**
           * How the site's labor is billed: never under `minimumMinutes`, and
           * rounded up to `incrementMinutes` (60 and 15: an hour minimum, then
           * quarter hours). Defaulted to 0 and 1, exact minutes, which is how
           * documents sent before the rule was frozen were priced, so their
           * prices do not move.
           */
          minimumMinutes: z.number().int().nonnegative().default(0),
          incrementMinutes: z.number().int().positive().default(1),
        })
        .strict()
        .nullable(),
      driveCharge: z
        .object({
          town: z.string(),
          price: moneyAmountSchema,
          taxable: z.boolean(),
          /** The town's drive-charge service in HCP. Null before it was frozen. */
          hcpServiceUuid: z.string().nullable().default(null),
        })
        .strict()
        .nullable(),
    })
    .strict(),
  repairs: z.array(irrigationRepairRowSchema),
})

/**
 * What was found in the irrigation, grouped by kind as tree problems are:
 * "Bad nozzles: 14, in zones 2, 4, 7" with the directory's description,
 * rather than an inventory of every head.
 */
export const irrigationFindingsBlockSchema = z.object({
  type: z.literal('irrigationFindings'),
  entries: z.array(
    z
      .object({
        name: z.string(),
        description: z.string().nullable(),
        /** How many, across every repair of this kind. */
        count: z.number().int().positive(),
        /** The zones it was found in, in the order first met. */
        zones: z.array(z.string()),
        /** The worst severity recorded, never an average. */
        worstSeverity: problemSeveritySchema.nullable(),
        /** A technician's photo of one, else the directory's stock photo. */
        photoDocumentId: uuidSchema.nullable(),
      })
      .strict(),
  ),
})

/** Labeled values: insurance limits, license numbers, certifications. */
export const factsBlockSchema = z.object({
  type: z.literal('facts'),
  title: z.string(),
  items: z.array(z.object({ label: z.string(), value: z.string() }).strict()),
})

/** Photographs keyed to tree numbers. */
export const photosBlockSchema = z.object({
  type: z.literal('photos'),
  title: z.string(),
  items: z.array(
    z
      .object({
        documentId: uuidSchema,
        fileName: z.string(),
        caption: z.string().nullable(),
      })
      .strict(),
  ),
})

/**
 * The signature block.
 *
 * `requiresBoardApproval` is carried explicitly because for an HOA it usually
 * does, and a signature line that ignores that fact invites someone without the
 * authority to sign it.
 */
export const acceptanceBlockSchema = z.object({
  type: z.literal('acceptance'),
  /**
   * The price of the work, before tax.
   *
   * The signature line adds the tax rate from the pricing block, so the
   * number beside the pen is the one being agreed to.
   */
  totalAmount: moneyAmountSchema.nullable(),
  validUntil: z.string().datetime().nullable(),
  signerLabel: z.string(),
  requiresBoardApproval: z.boolean(),
  note: z.string().nullable(),
  /**
   * The figure is a floor, because some trees in scope carry no estimate.
   *
   * Carried on this block and not just on the pricing table: this is the line
   * somebody signs, and a number presented as final beside a signature is the
   * version that gets held to. If the total is provisional, the sentence saying
   * so has to sit where the pen does.
   */
  totalIsProvisional: z.boolean(),
})

/**
 * What a site's survey says about it since the visit before: the problems
 * found again, and what changed on the property (docs/site-visits.md,
 * "Proposals"). By value, like everything frozen, and every field
 * defaulted so the block can grow without un-freezing a sent proposal.
 */
export const sinceLastVisitContentSchema = z.object({
  /** The day of the visit before, `YYYY-MM-DD`. */
  since: z.string(),
  /**
   * This visit's problems that were found the visit before too ("Still
   * here"), one entry per kind of problem.
   */
  cameBack: z
    .array(
      z
        .object({
          /** "Mistletoe", "Bad nozzle". */
          name: z.string(),
          /** Where: the trees ("#41"), or the zones and parts ("Zone 2"). */
          where: z.array(z.string()),
          /** When it was found before, `YYYY-MM-DD`. */
          foundOn: z.string(),
        })
        .strict(),
    )
    .default([]),
  /** What really changed on the property since, a sentence each. */
  changes: z.array(z.string()).default([]),
})

export type SinceLastVisitContent = z.infer<typeof sinceLastVisitContentSchema>

export const sinceLastVisitBlockSchema = sinceLastVisitContentSchema.extend({
  type: z.literal('sinceLastVisit'),
  siteName: z.string(),
  /** The section's wording as it was when the document was built. */
  title: z.string().nullable().default(null),
  paragraphs: z.array(z.string()).default([]),
})

/**
 * Work recommended for a later season, priced as an estimate
 * (docs/seasonal-work.md).
 *
 * Its own block rather than more rows in the schedule, because nothing in it
 * can be chosen: the client approves the next visit, and this is rebid when
 * its season comes. The plans, the adjustments and the price never read it.
 *
 * In the order the year brings the seasons after the next visit, then by
 * site, then by tree in schedule order.
 */
export const futureWorkBlockSchema = z.object({
  type: z.literal('futureWork'),
  /** The section's wording as it was when the document was built. */
  title: z.string().nullable(),
  /** The note the asterisk on each estimate points to. */
  paragraphs: z.array(z.string()),
  seasons: z.array(
    z
      .object({
        season: seasonSchema,
        sites: z.array(
          z
            .object({
              /**
               * Which site, by id: identity only, never shown. What a reminder
               * of this season's work is recorded against when the proposal
               * is sent. Absent where the document was built without ids.
               */
              siteId: z.string().nullable().optional(),
              siteName: z.string(),
              trees: z.array(
                z
                  .object({
                    /** Which tree, by id: identity only, never shown. */
                    treeId: z.string().nullable(),
                    label: z.string(),
                    species: treeSpeciesMarkSchema.nullable(),
                    /**
                     * The pruning that season, priced at the crew rate. Null
                     * when the tree is not pruned that season.
                     */
                    pruning: z
                      .object({
                        jobs: z.array(
                          z
                            .object({ type: jobTypeSchema, level: jobLevelSchema })
                            .strict(),
                        ),
                        amount: moneyAmountSchema.nullable(),
                      })
                      .strict()
                      .nullable(),
                    /**
                     * The treatments that season, by problem. A treatment's
                     * amount is null when its problem was priced by hand as
                     * one figure, which the tree's amount counts once.
                     */
                    treatments: z.array(
                      z
                        .object({
                          problemName: z.string(),
                          label: z.string(),
                          amount: moneyAmountSchema.nullable(),
                        })
                        .strict(),
                    ),
                    /**
                     * Problems dealt with that season that have no treatment
                     * chosen, by name ("Mistletoe"): the reason the tree is on
                     * that season's list, priced only when the arborist set a
                     * price for it by hand. `.default([])` so a proposal
                     * frozen before this still parses.
                     */
                    findings: z.array(z.string()).default([]),
                    /**
                     * Removal, when it is quoted: instead of the pruning when
                     * there is some, otherwise the work itself.
                     */
                    removal: z
                      .object({
                        amount: moneyAmountSchema.nullable(),
                        notes: z.string().nullable(),
                      })
                      .strict()
                      .nullable(),
                    /**
                     * What the tree adds to the estimate: the pruning and
                     * treatments, or the removal when nothing is pruned.
                     */
                    amount: moneyAmountSchema.nullable(),
                    isOptional: z.boolean(),
                  })
                  .strict(),
              ),
              amount: moneyAmountSchema.nullable(),
            })
            .strict(),
        ),
        /** The season's estimate, before tax. Null when nothing priced. */
        amount: moneyAmountSchema.nullable(),
      })
      .strict(),
  ),
})

export const proposalBlockSchema = z.discriminatedUnion('type', [
  coverBlockSchema,
  summaryBlockSchema,
  proseBlockSchema,
  siteMapBlockSchema,
  treeScheduleBlockSchema,
  jobLegendBlockSchema,
  problemsBlockSchema,
  pricingBlockSchema,
  factsBlockSchema,
  photosBlockSchema,
  acceptanceBlockSchema,
  irrigationScheduleBlockSchema,
  irrigationFindingsBlockSchema,
  sinceLastVisitBlockSchema,
  futureWorkBlockSchema,
])

export type ProposalBlock = z.infer<typeof proposalBlockSchema>

export type CoverBlock = z.infer<typeof coverBlockSchema>
export type SummaryBlock = z.infer<typeof summaryBlockSchema>
export type ProseBlock = z.infer<typeof proseBlockSchema>
export type SiteMapBlock = z.infer<typeof siteMapBlockSchema>
export type TreeScheduleBlock = z.infer<typeof treeScheduleBlockSchema>
export type TreeScheduleRow = z.infer<typeof treeScheduleRowSchema>
export type JobLegendBlock = z.infer<typeof jobLegendBlockSchema>
export type ProblemsBlock = z.infer<typeof problemsBlockSchema>
export type PricingBlock = z.infer<typeof pricingBlockSchema>
export type FactsBlock = z.infer<typeof factsBlockSchema>
export type PhotosBlock = z.infer<typeof photosBlockSchema>
export type AcceptanceBlock = z.infer<typeof acceptanceBlockSchema>
export type IrrigationScheduleBlock = z.infer<
  typeof irrigationScheduleBlockSchema
>
export type IrrigationRepairRow = z.infer<typeof irrigationRepairRowSchema>
export type IrrigationFindingsBlock = z.infer<
  typeof irrigationFindingsBlockSchema
>
export type SinceLastVisitBlock = z.infer<typeof sinceLastVisitBlockSchema>
export type FutureWorkBlock = z.infer<typeof futureWorkBlockSchema>

/**
 * A whole proposal, ready to render.
 *
 * `isFrozen` says which of two things you are looking at. A draft is resolved
 * from live data on every read, so editing a survey changes it. A sent version
 * is the copy that was stored the moment it went out — archiving a tree the
 * following week must not alter a document somebody already has in their inbox,
 * and a proposal that quietly disagrees with the PDF attached to a board's
 * minutes is worse than no record at all.
 */
export const proposalDocumentDtoSchema = z.object({
  versionId: uuidSchema,
  versionNumber: z.number().int().positive(),
  isFrozen: z.boolean(),
  /** When a frozen document was captured. Null while it is still a draft. */
  frozenAt: z.string().datetime().nullable(),
  /**
   * Whether the client may decline individual pruning job types.
   *
   * Frozen here rather than read live, because what a client may choose is
   * part of the offer they were made. Turning the setting off stops it for
   * new proposals; one somebody is already looking at keeps the terms it was
   * sent with.
   *
   * `.default(true)` — documents frozen before this existed offered the
   * choice, and a required key would make every one of them fail to parse.
   */
  clientMaySelectJobTypes: z.boolean().default(true),
  /**
   * Whether pricing is broken out by area name.
   *
   * A staff choice, frozen with the document. The client cannot change it:
   * the bill they were shown is the bill.
   *
   * `.default(false)` — documents frozen before areas existed carry no key,
   * and were not broken out.
   */
  itemizeByArea: z.boolean().default(false),
  /**
   * What kinds of work the proposal carries, chosen by staff per proposal.
   *
   * Defaulted for documents frozen before irrigation could be bid, which
   * carry neither key and were trees only. The default lives here rather
   * than at each reader, so a reader cannot forget it.
   */
  includesTrees: z.boolean().default(true),
  includesIrrigation: z.boolean().default(false),
  blocks: z.array(proposalBlockSchema),
})

export type ProposalDocumentDto = z.infer<typeof proposalDocumentDtoSchema>
